import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
    faArrowLeft,
    faUser,
    faPhone,
    faMapMarkerAlt,
    faMoneyBillWave,
    faCalendarDays,
    faFloppyDisk,
    faCircleExclamation,
    faCircleCheck,
} from "@fortawesome/free-solid-svg-icons";

import { useHerKart } from "./hooks/useHerKart";

// Fallback demo orders — used until "Load Live Order" is clicked
const sampleOrders = [
  {
    orderNo: "FH20260710001",
    orderedAt: "2026-07-10T10:30:00Z",
    status: "Pending",
    remarks: "",
    paymentMode: "COD",
    totalItems: 4,
    subtotal: 1090,
    delivery: 49,
    grandTotal: 1139,
    customer: {
      name: "Priya Sharma",
      mobile: "9876543210",
      house: "Flat 204, Lotus Residency",
      street: "MG Road",
      landmark: "Near City Mall",
      city: "Mumbai",
      state: "Maharashtra",
      pincode: "400001",
    },
    items: [
      { _id: "i1", name: "Handmade Cotton Bag", store: "Priya Creations", quantity: 2, price: 350, image: "https://picsum.photos/id/1015/200/200" },
      { _id: "i2", name: "Organic Soap Pack", store: "Nature Bliss", quantity: 1, price: 199, image: "https://picsum.photos/id/201/200/200" },
      { _id: "i3", name: "Bamboo Water Bottle", store: "Eco Living", quantity: 1, price: 240, image: "https://picsum.photos/id/106/200/200" },
    ],
  },
];

const HerKartOrderDetails = () => {
  const { orderNo } = useParams();
  const navigate = useNavigate();
  const authState = useSelector((state) => state.authReducer);

  const { fetchOrderByNo, updateOrder } = useHerKart();

  const [order, setOrder] = useState(null);
  const [status, setStatus] = useState("Pending");
  const [remarks, setRemarks] = useState("");

  const [isLive, setIsLive] = useState(false);
  const [loadingOrder, setLoadingOrder] = useState(false);
  const [saving, setSaving] = useState(false);

  // Inline UI message (replaces alert())
  const [message, setMessage] = useState(null);

  // useEffect(() => {
  //   const selected =
  //     sampleOrders.find((item) => item.orderNo === orderNo) || sampleOrders[0];

  //   if (!selected) {
  //     navigate("/herkart/orders", { replace: true });
  //     return;
  //   }

  //   setOrder(selected);
  //   setStatus(selected.status || "Pending");
  //   setRemarks(selected.remarks || "");
  // }, [orderNo, navigate]);

  useEffect(() => {
    // Demo fallback temporarily disabled — kept for future demo use.
    // const selected =
    //   sampleOrders.find((item) => item.orderNo === orderNo) || sampleOrders[0];
    // if (!selected) {
    //   navigate("/herkart/orders", { replace: true });
    //   return;
    // }
    // setOrder(selected);
    // setStatus(selected.status || "Pending");
    // setRemarks(selected.remarks || "");

    loadLiveOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderNo]);

  const orderDate = useMemo(() => {
    if (!order) return "";
    return new Date(order.orderedAt).toLocaleString();
  }, [order]);

  // NEW — this page is now reachable from both the seller's Orders list AND the
  // customer's My Orders list. A seller only ever sees/edits their own line items;
  // the customer (order owner) sees the full order, read-only.
  const isProvider = useMemo(() => {
    if (!order?.items?.length) return false;
    const sellerCode = authState?.user?.think5whyscode;
    if (!sellerCode) return false;
    return order.items.some((item) => item.sellerCode === sellerCode);
  }, [order, authState]);

  const visibleItems = isProvider ? order?.sellerItems || order?.items || [] : order?.items || [];
  const visibleSubtotal = isProvider ? order?.sellerSubtotal ?? order?.subtotal : order?.subtotal;

  // const loadLiveOrder = async () => {
  //   setLoadingOrder(true);
  //   setMessage(null);
  //   try {
  //     const response = await fetchOrderByNo(orderNo);
  //     if (response?.success && response.order) {
  //       setOrder(response.order);
  //       setStatus(response.order.status || "Pending");
  //       setRemarks(response.order.remarks || "");
  //       setIsLive(true);
  //     } else {
  //       setMessage({ type: "error", text: "Live order not found. Showing sample order." });
  //     }
  //   } catch (e) {
  //     console.error("Order fetch error:", e);
  //     setMessage({ type: "error", text: "Could not load the live order. Please try again." });
  //   } finally {
  //     setLoadingOrder(false);
  //   }
  // };

  const loadLiveOrder = async () => {
    setLoadingOrder(true);
    setMessage(null);
    try {
      const response = await fetchOrderByNo(orderNo);
      if (response?.success && response.order) {
        setOrder(response.order);
        setStatus(response.order.status || "Pending");
        setRemarks(response.order.remarks || "");
        setIsLive(true);
      } else {
        setMessage({ type: "error", text: "Order not found." });
      }
    } catch (e) {
      console.error("Order fetch error:", e);
      setMessage({ type: "error", text: "Could not load the order. Please try again." });
    } finally {
      setLoadingOrder(false);
    }
  };

  const handleSave = async () => {
    setMessage(null);

    if (isLive) {
      setSaving(true);
      try {
        const response = await updateOrder(orderNo, { status, remarks });
        if (response?.success) {
          setOrder(response.order);
          setMessage({ type: "success", text: "Order updated successfully." });
        } else {
          setMessage({ type: "error", text: "Failed to update order. Please try again." });
        }
      } catch (e) {
        console.error("Order update error:", e);
        setMessage({ type: "error", text: "Failed to update order. Please try again." });
      } finally {
        setSaving(false);
      }
      return;
    }

    // Demo mode: update in-memory only, no persistence
    setOrder((prev) => ({ ...prev, status, remarks }));
    setMessage({ type: "success", text: "Order updated (demo mode — not saved to server)." });
  };

  if (!order) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#050816] text-white">
        <p className="text-slate-300">{loadingOrder ? "Loading order..." : "Order not found."}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050816] text-white">
      <div className="sticky top-0 z-40 border-b border-white/10 bg-[#050816]/95 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center gap-3 px-4 py-4">
          <Link
            to={isProvider ? "/herkart/orders" : "/herkart/orders/mine"}
            className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 hover:bg-white/20"
          >
            <FontAwesomeIcon icon={faArrowLeft} />
          </Link>

          <div className="flex-1 justify-start flex-col">
            <h1 className="text-xl font-bold text-left">{order.orderNo}</h1>

            <p className="text-xs text-slate-400">
              {isProvider ? "Merchant Order Details" : "Order Details"}
            </p>
          </div>

          <button
            onClick={loadLiveOrder}
            className="rounded-xl bg-white/10 w-fit px-2 py-1 text-xs hover:bg-white/20"
          >
            {/* {loadingOrder ? "Loading..." : isLive ? "🔄 Live" : "🔄 Load Live Order"} */}
            {loadingOrder ? "Loading..." : "🔄 Refresh"}
          </button>
        </div>
      </div>

      {message && (
        <div className="mx-auto max-w-7xl px-4 pt-4">
          <div
            className={`flex items-center gap-3 rounded-2xl border p-4 text-sm ${
              message.type === "success"
                ? "border-emerald-500/20 bg-emerald-500/10 text-emerald-200"
                : "border-red-500/20 bg-red-500/10 text-red-200"
            }`}
          >
            <FontAwesomeIcon
              icon={message.type === "success" ? faCircleCheck : faCircleExclamation}
            />
            {message.text}
          </div>
        </div>
      )}

      <div className="mx-auto grid max-w-7xl gap-8 px-4 py-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <div className="rounded-3xl border border-white/10 bg-white/5 p-6">
            <h2 className="mb-5 text-xl font-bold text-cyan-200">Customer Details</h2>

            <div className="grid gap-5 md:grid-cols-2">
              <div className="flex items-start gap-3">
                <FontAwesomeIcon icon={faUser} className="mt-1 text-pink-400" />

                <div>
                  <p className="text-xs text-slate-400">Customer</p>

                  <p className="font-semibold text-gray-100">{order.customer.name}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <FontAwesomeIcon icon={faPhone} className="mt-1 text-pink-400" />

                <div>
                  <p className="text-xs text-slate-200">Mobile</p>

                  <p className="font-semibold text-gray-200">{order.customer.mobile}</p>
                </div>
              </div>

              <div className="md:col-span-2 flex items-start gap-3">
                <FontAwesomeIcon icon={faMapMarkerAlt} className="mt-1 text-pink-400" />

                <div>
                  <p className="text-xs text-slate-400">Delivery Address</p>

                  <p className="font-semibold leading-7 text-gray-100">
                    {order.customer.house}, {order.customer.street}
                    {order.customer.landmark
                      ? `, ${order.customer.landmark}`
                      : ""}
                    <br />
                    {order.customer.city}, {order.customer.state} -{" "}
                    {order.customer.pincode}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-3xl border border-white/10 bg-white/5 p-6">
            <h2 className="mb-5 text-xl font-bold text-cyan-200">
              {isProvider ? "Your Items In This Order" : "Ordered Products"}
            </h2>

            <div className="space-y-4">
              {visibleItems.map((item, index) => (
                <div
                  key={item.productId || index}
                  className="flex items-center gap-4 rounded-2xl border text-gray-100 border-white/10 bg-white/5 p-4"
                >
                  <img
                    src={item.image}
                    alt={item.name}
                    className="h-20 w-20 rounded-xl object-cover"
                  />

                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-100">{item.name}</h3>

                    <p className="mt-1 text-sm text-slate-200">{item.store}</p>

                    <p className="mt-1 text-sm text-slate-200">
                      Qty : {item.quantity}
                    </p>
                  </div>

                  <div className="text-right">
                    <p className="text-lg font-bold text-pink-400">
                      ₹{item.price * item.quantity}
                    </p>

                    <p className="text-xs text-slate-300">₹{item.price} each</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div>
          <div className="sticky top-24 rounded-3xl border border-white/10 bg-white/5 p-6">
            <h2 className="text-xl font-bold text-cyan-200">Order Summary</h2>

            <div className="mt-6 space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-slate-200">Items</span>

                <span>{visibleItems.length}</span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-slate-200">
                  {isProvider ? "Your Subtotal" : "Subtotal"}
                </span>

                <span>₹{visibleSubtotal}</span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-slate-200">Delivery</span>

                <span>₹{order.delivery}</span>
              </div>

              <div className="flex items-center justify-between border-t border-white/10 pt-4 text-xl font-bold">
                <span>Total</span>

                <span className="text-pink-400">₹{order.grandTotal}</span>
              </div>
            </div>

            <div className="mt-6 rounded-2xl border border-emerald-500/20 bg-emerald-500/10 p-4">
              <div className="flex items-start gap-3">
                <FontAwesomeIcon icon={faMoneyBillWave} className="mt-1 text-emerald-400" />

                <div>
                  <h4 className="font-semibold text-gray-100">Payment Mode</h4>

                  <p className="text-sm text-slate-100">{order.paymentMode}</p>
                </div>
              </div>
            </div>

            <div className="mt-4 rounded-2xl border border-white/10 bg-white/5 p-4">
              <div className="flex items-start gap-3">
                <FontAwesomeIcon icon={faCalendarDays} className="mt-1 text-pink-400" />

                <div>
                  <h4 className="font-semibold text-gray-100">Ordered On</h4>

                  <p className="text-sm text-slate-200">{orderDate}</p>
                </div>
              </div>
            </div>

            {isProvider ? (
              <>
                <div className="mt-6">
                  <label className="mb-2 block text-sm text-slate-200">
                    Order Status
                  </label>

                  <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    className="w-full rounded-xl border text-gray-100 border-white/10 bg-[#0b1120] px-2 py-1 outline-none focus:border-pink-500"
                  >
                    <option value="Pending">Pending</option>
                    <option value="Delivered">Delivered</option>
                    <option value="Partially Delivered">Partially Delivered</option>
                    <option value="Cancelled">Cancelled</option>
                  </select>
                </div>

                <div className="mt-5">
                  <label className="mb-2 block text-sm text-slate-300">
                    Remarks
                  </label>

                  <input
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value.slice(0, 100))}
                    maxLength={100}
                    placeholder="Maximum 100 characters..."
                    className="w-full resize-none rounded-xl border border-white/10 bg-[#0b1120] px-4 py-3 outline-none focus:border-pink-500"
                  />

                  <div className="mt-1 text-right text-xs text-slate-500">
                    {remarks.length}/100
                  </div>
                </div>

                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="mt-4 flex items-center justify-center gap-2 rounded-2xl bg-pink-600 w-fit px-2 py-1 font-semibold transition hover:bg-pink-500 disabled:opacity-60"
                >
                  <FontAwesomeIcon icon={faFloppyDisk} />
                  {saving ? "Saving..." : "Save Changes"}
                </button>
              </>
            ) : (
              <div className="mt-6 rounded-2xl border border-white/10 bg-white/5 p-4">
                <p className="text-xs uppercase tracking-wide text-slate-500">Order Status</p>
                <p className="mt-1 font-semibold text-pink-300">{order.status}</p>
                {order.remarks && (
                  <p className="mt-2 text-sm text-slate-300">
                    <span className="font-semibold text-pink-300">Seller remarks: </span>
                    {order.remarks}
                  </p>
                )}
              </div>
            )}

            <Link
              to={`/herkart/bill/${orderNo}`}
              className="mt-3 flex items-center justify-center gap-2 rounded-2xl border border-pink-500/40 bg-pink-500/10 w-fit px-2 py-1 font-semibold text-pink-300 transition hover:bg-pink-500/20"
            >
              View Bill
            </Link>

            <Link
              to={isProvider ? "/herkart/orders" : "/herkart/orders/mine"}
              className="mt-3 flex w-fit items-center justify-center rounded-2xl border border-white/10 px-2 py-1 transition hover:bg-white/10"
            >
              Back to Orders
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HerKartOrderDetails;